import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import cx from "classnames";
import _ from "lodash";
import {
  Button,
  Form,
  Modal,
  Row,
  Col,
  InputGroup,
  Offcanvas,
  Dropdown,
} from "react-bootstrap";

import {
  createPersonalizedAliases,
  creatingPersonalizationLead,
  fetchingPersonalizationContacts,
  uploadingPersonalizationLead,
} from "actions/personalization";
import StoryboardContext from "components/Storyboard/context";

import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { leadReportHeader } from "constants/workspaceReportsData";
import {
  EmptyMessage,
  LoadingMessage,
} from "pages/storyboards/storyboardSettings/leadReports/components";
import { fetchingWorkspaceLeadReports } from "actions/workspaceReports";
import { PaginationForReports } from "pages/storyboards/storyboardSettings/workspaceReports";

import styles from "./modals.module.scss";
import { act } from "react";
import { createPageAliases } from "actions/aliases";

export const LeadCreation = ({ show, onClose, handleFetchLeads }) => {
  const dispatch = useDispatch();

  const formRef = useRef(null);
  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardId = useSelector((state) =>
    _.get(
      state,
      "storyboards.storyboard.item.id",
      _.get(storyboardContext, "id")
    )
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    workPhone: "",
    title: "",
    company: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show) {
      // Reset form state when modal is hidden

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        workPhone: "",
        title: "",
        company: "",
      });
      setErrors({});
    }
  }, [show]);

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.id]: e.target.value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[a-zA-Z0-9][\w.-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!emailRegex.test(formData.email)) {
      newErrors.email = "Enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleValidateAndSave = () => {
    if (validateForm()) {
      const request = [formData];
      dispatch(
        creatingPersonalizationLead(storyboardId, request, false, () => {
          handleFetchLeads(0);
        })
      );
      onClose();
    }
  };

  return (
    <Modal
      contentClassName={styles.leadCreationWrapper}
      show={show}
      dialogClassName={styles.leadCreateDialog}
      size="lg"
      centered
    >
      <Modal.Header className={styles.header}>
        <span className={styles.title}>Create New Lead</span>
        <Button
          variant="info"
          onClick={() => onClose()}
          className={styles.closeButton}
        >
          <i className={cx("cleverstory-close", styles.closeButtonIcon)} />
        </Button>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <Form noValidate ref={formRef}>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="firstname">
                <Form.Label className={styles.formFieldLabel}>
                  First Name
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  className={styles.formFields}
                  id="firstName"
                  required
                  value={formData.firstName}
                  onChange={handleChange}
                  isInvalid={!!errors.firstName}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.firstName}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="lastname">
                <Form.Label className={styles.formFieldLabel}>
                  Last Name
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  className={styles.formFields}
                  id="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="workmail">
                <Form.Label className={styles.formFieldLabel}>
                  Work Mail
                </Form.Label>
                <Form.Control
                  type="text"
                  required
                  id="email"
                  pattern="^[a-zA-Z0-9][a-zA-Z0-9._%+-]*@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"
                  className={styles.formFields}
                  value={formData.email}
                  isInvalid={!!errors.email}
                  onChange={handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="account">
                <Form.Label className={styles.formFieldLabel}>
                  Account/Company Name
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  id="company"
                  className={styles.formFields}
                  value={formData.company}
                  onChange={handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter the company name.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="website">
                <Form.Label className={styles.formFieldLabel}>
                  Phone No.
                </Form.Label>
                <Form.Control
                  type="tel"
                  placeholder=""
                  id="workPhone"
                  pattern="^\d+$"
                  className={styles.formFields}
                  value={formData.website}
                  onChange={handleChange}
                />
                <Form.Control.Feedback type="invalid">
                  Please enter a valid phone number.
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="designation">
                <Form.Label className={styles.formFieldLabel}>
                  Designation
                </Form.Label>
                <Form.Control
                  type="text"
                  placeholder=""
                  id="title"
                  className={styles.formFields}
                  value={formData.title}
                  onChange={handleChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Form>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <Button onClick={handleValidateAndSave} variant="primary">
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const LeadSelection = ({ show, onClose, handleFetchLeads }) => {
  const dispatch = useDispatch();

  const [search, setSearch] = useState("");
  const [selectedLeads, setSelectedLeads] = useState([]);

  const storyboardContext = React.useContext(StoryboardContext);
  const storyboardId = useSelector((state) =>
    _.get(
      state,
      "storyboards.storyboard.item.id",
      _.get(storyboardContext, "id")
    )
  );
  const fields = [
    { value: "firstName", name: "First Name" },
    { value: "lastName", name: "Last Name" },
    { name: "Work Mail", value: "email" },
    { value: "company", name: "Company" },
    { name: "Designation", value: "title" },
    { name: "Work Phone", value: "workPhone" },
  ];

  const loading = useSelector((state) =>
    _.get(state, `personalization.contacts.loading`, false)
  );
  const data = useSelector((state) =>
    _.get(state, `personalization.contacts.items`, [])
  );
  const pagination = useSelector((state) =>
    _.get(state, "personalization.contacts.pagination", {})
  );

  const leadsData = useSelector((state) => {
    const records = Array.isArray(data) ? data : [];
    return records;
  });

  useEffect(() => {
    handleFetchContacts(0);
  }, [search]);

  useEffect(() => {
    if (!show) {
      // Reset form state when modal is hidden
      setSelectedLeads([])
    }
    handleFetchContacts(0)
  }, [show]);

  const loadMore = (page) => {
    if (page >= pagination.totalPages) {
      return;
    }
    handleFetchContacts(page);
  };

  const handleFetchContacts = (page) => {
    const request = {
      query: search,
    };
    dispatch(fetchingPersonalizationContacts(request, page, 20));
  };

  const updatedLeadReportHeader = useMemo(() => {
    return fields.map((f) => {
      const match = leadReportHeader.find((header) => header.field === f.value);
      return (
        match || {
          name: f.name,
          field: f.value,
          uid: f.value,
        }
      );
    });
  }, [fields]);

  const handleUploadLeads = () => {
    const payload = {
      personalizationRequests: [],
      createContactRequests: [],
    };

    if (_.isEmpty(selectedLeads)) {
      return;
    }

    selectedLeads.forEach((leadData) => {
      if (leadData.id) {
        // Add to personalizationRequests
        payload.personalizationRequests.push({
          contact: leadData?.id,
          storyboard: storyboardId,
          name: `${_.get(leadData, 'firstName', "")}${_.get(leadData, 'lastName', '')}`,
          mappings: [], // Fill this later if needed
        });
      } else {
        // Add to createContactRequests
        payload.createContactRequests.push({
          firstName: leadData?.firstName || "",
          lastName: leadData?.lastName || "",
          email: leadData?.email || "",
          title: leadData?.title || "",
          company: leadData?.company || "",
          workPhone: leadData?.workPhone || "",
          mobilePhone: leadData?.mobilePhone || null,
        });
      }
    });

    dispatch(
      uploadingPersonalizationLead(storyboardId, payload, () => {
        handleFetchLeads(0);
      })
    );
    onClose();
  };

  const renderFieldData = (leadData, headerField) => {
    switch (true) {
      default:
        if (headerField.uid != null) {
          if (leadData[headerField.uid] !== undefined) {
            return leadData[headerField.uid] || "-";
          }
          if (leadData && leadData[headerField.uid] !== undefined) {
            return leadData[headerField.uid] || "-";
          }
          return "";
        } else {
          const customField = Array.isArray(leadData?.fields)
            ? leadData.fields.find((field) => field.field === headerField.field)
            : null;
          if (customField) {
            if (typeof customField.values === "boolean") {
              return customField.values.toString() || "-";
            }
            if (Array.isArray(customField.values)) {
              return customField.values.length > 0
                ? customField.values.join(", ")
                : "-";
            }
            return customField.values || "-";
          }
          return "-";
        }
    }
  };

  const handleLeadToggle = (lead, checked) => {
    setSelectedLeads((prev) => {
      if (checked) {
        // Add if not already added
        const alreadyExists = prev.some((item) => item.id === lead.id);
        return alreadyExists ? prev : [...prev, lead];
      } else {
        // Remove on uncheck
        return prev.filter((item) => item.id !== lead.id);
      }
    });
  };

  return (
    <Modal
      contentClassName={styles.leadSelectionWrapper}
      show={show}
      dialogClassName={styles.leadSelectionDialog}
      size="lg"
      centered
      onHide={() => {
        onClose()
      }}
    >
      <Modal.Header className={styles.header}>
        <span className={styles.title}>Leads</span>
        <div className={styles.actionContainer}>
          <InputGroup className={styles.searchBox}>
            <InputGroup.Text className={styles.icon}>
              <i className={cx("cleverstory-search")} />
            </InputGroup.Text>
            <Form.Control
              placeholder="Search"
              className={styles.inputField}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </InputGroup>
          <Button
            variant="info"
            onClick={() => {
              setSearch("")
              onClose()
            }}
            className={styles.closeButton}
          >
            <i className={cx("cleverstory-close", styles.closeButtonIcon)} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <div className={styles.tableOverflow}>
          <table className={cx(styles.leadsAnalyticsTable)}>
            {loading && (
              <div className={cx(styles.loader, styles.modalLoader)}>
                <LoadingMessage className={styles.loadingMessage} />
              </div>
            )}

            {!loading && data.length == 0 && (
              <div className={cx(styles.loader, styles.modalLoader)}>
                <EmptyMessage />
              </div>
            )}

            {!loading && data && data.length > 0 && (
              <>
                <thead className={cx(styles.tableHeader, styles.stickyHeader)}>
                  <tr className={styles.headerRow}>
                    <td
                      className={cx(styles.tableBodyData, styles.stickyColumn)}
                    ></td>
                    {fields.map((column, key) => {
                      return (
                        <th
                          key={key}
                          className={cx(styles.headerData, {
                            [styles.stickyColumn]: key === 0,
                          })}
                        >
                          {column.name}
                        </th>
                      );
                    })}
                  </tr>
                </thead>

                <tbody className={styles.tableBody}>
                  {leadsData.map((lead, rowIndex) => {
                    return (
                      <tr key={rowIndex} className={styles.tableBodyDataRow}>
                        <td
                          className={cx(
                            styles.tableBodyData,
                            styles.stickyColumn
                          )}
                        >
                          <Form.Check
                            className={styles.checkbox}
                            type="checkbox"
                            checked={selectedLeads.some(
                              (item) => item.id === lead.id
                            )}
                            onChange={(e) =>
                              handleLeadToggle(lead, e.target.checked)
                            }
                          />
                        </td>
                        {updatedLeadReportHeader.map((field, index) => (
                          <td
                            key={index}
                            className={cx(styles.tableBodyData, {
                              [styles.stickyColumn]: index === 0,
                            })}
                          >
                            <TruncatedTextWithTooltip
                              content={renderFieldData(lead, field)}
                              maxWidth={250}
                              placement="top"
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </>
            )}
          </table>
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <PaginationForReports
          className={styles.paginationreport}
          pagination={pagination}
          type={"Leads"}
          onClick={loadMore}
        />
        <Button
          className={styles.saveButton}
          onClick={() => {
            handleUploadLeads();
          }}
          variant="primary"
        >
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export const LeadDetailView = ({ show, onClose, leadData }) => {
  const renderInitials = (name) => {
    let firstName = _.get(name, "firstName") || "-";
    let lastName = _.get(name, "lastName") || "";

    let initial = "";

    if (name) {
      initial = firstName.charAt(0);
    }

    if (lastName) {
      initial = initial + lastName.charAt(0);
    }

    return initial.toUpperCase();
  };

  return (
    <Offcanvas show={show} className={styles.leadInfoWrapper}>
      <Offcanvas.Header>
        <span className={styles.title}>Lead Information</span>
        <Button
          variant="info"
          onClick={() => onClose()}
          className={styles.closeButton}
        >
          <i className={cx("cleverstory-close", styles.closeButtonIcon)} />
        </Button>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className={styles.details}>
          <div className={styles.leadInitials}>{renderInitials(leadData)}</div>
          <div className={styles.data}>
            <TruncatedTextWithTooltip content={`${leadData.firstName} ${leadData.lastName}`} maxWidth={270} placement='top' className={styles.name} />
            <TruncatedTextWithTooltip content={leadData.email} maxWidth={270} placement='top' className={styles.mail} />
          </div>
        </div>
        <div className={styles.aboutSection}>
          <span className={styles.title}>About this contact</span>
          <div className={styles.detailSection}>
            <div className={styles.container}>
              <span className={styles.label}>Email</span>
              <TruncatedTextWithTooltip content={_.get(leadData, "email", "-")} maxWidth={270} placement='top' className={styles.value} />
            </div>
            <div className={styles.container}>
              <span className={styles.label}>Account/Company Name</span>
              <TruncatedTextWithTooltip content={_.get(leadData, "company", "-")} maxWidth={270} placement='top' className={styles.value} />
            </div>
            <div className={styles.container}>
              <span className={styles.label}>Designation</span>
              <TruncatedTextWithTooltip content={_.get(leadData, "title", "-")} maxWidth={270} placement='top' className={styles.value} />
            </div>
            <div className={styles.container}>
              <span className={styles.label}>Phone No.</span>
              <TruncatedTextWithTooltip content={_.get(leadData, "workPhone", "-")} maxWidth={270} placement='top' className={styles.value} />
            </div>
          </div>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export const GenerateLinkView = ({ show, onClose, activeLead }) => {
  const dispatch = useDispatch();
  const activeContact = _.get(activeLead, "contact");
  const pages = useSelector((state) => _.get(state, "pages.items", []));
  const [selectedPage, setSelectedPage] = useState(pages[0]);
  const creatingPersonalizationLink = useSelector((state) =>
    _.get(state, `personalization.links.creating`, false)
  );

  const renderInitials = (name) => {
    let firstName = _.get(name, "firstName") || "-";
    let lastName = _.get(name, "lastName") || "";

    let initial = "";

    if (name) {
      initial = firstName.charAt(0);
    }

    if (lastName) {
      initial = initial + lastName.charAt(0);
    }

    return initial.toUpperCase();
  };

  useEffect(() => {
    if (!_.isEmpty(pages) && _.size(pages) > 0) {
      setSelectedPage(pages[0]);
    }
  }, [pages]);

  const handlePageChange = (page) => {
    if (!_.isEqual(page, selectedPage)) {
      setSelectedPage(page);
    }
  };

  useEffect(()=>{
    if(!show){
      setSelectedPage(pages[0])
    }
  },[show])

  const handleSaveLink = () => {
    const request = {
      type: "page",
      autoCreate: true,
      canonical: false,
      deletable: false,
      target: selectedPage.id,
      personalization: activeLead.id,
      contact: _.get(activeContact, 'id'),
    };
    dispatch(createPersonalizedAliases(request));
    onClose();
  };

  return (
    <Offcanvas
      placement="end"
      show={show}
      className={cx(styles.generateLinkWrapper)}
    >
      <Offcanvas.Header>
        <span className={styles.title}>Generate Link</span>
        <Button
          variant="info"
          onClick={() => onClose()}
          className={styles.closeButton}
        >
          <i className={cx("cleverstory-close", styles.closeButtonIcon)} />
        </Button>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <div className={styles.details}>
          <div className={styles.leadInitials}>
            {renderInitials(activeContact)}
          </div>
          <div className={styles.data}>
            <TruncatedTextWithTooltip content={activeContact?.firstName} maxWidth={270} placement='top' className={styles.name} />
            <TruncatedTextWithTooltip content={activeContact?.email} maxWidth={270} placement='top' className={styles.mail} />
          </div>
        </div>
        <div className={styles.actionContainer}>
          <div className={styles.container}>
            <span className={styles.title}>Choose the Page</span>
            <span className={styles.description}>
              Create a personalised link for any page
            </span>
          </div>
          <Dropdown className={cx(styles.filterOptionDropdown)}>
            <Dropdown.Toggle>
              {selectedPage ?
                <TruncatedTextWithTooltip content={_.get(selectedPage, 'name')} maxWidth={300} placement='top'/>
              : "select Page"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="min-width-0">
              {!_.isEmpty(pages) &&
                _.map(pages, (page) => {
                  return (
                    <Dropdown.Item
                      active={selectedPage?.id === page.id}
                      data-track-event="Mixpanel" data-track-props="Choose the Page" data-track-id={activeLead?.id} data-track-page={selectedPage?.id}
                      onClick={() => {
                        handlePageChange(page);
                      }}
                    >
                      <TruncatedTextWithTooltip content={_.get(page, 'name')} maxWidth={350} placement='top'/>
                    </Dropdown.Item>
                  );
                })}
            </Dropdown.Menu>
          </Dropdown>
          <Button
            disabled={creatingPersonalizationLink}
            variant="primary"
            onClick={() => {
              handleSaveLink();
            }}
            className={styles.generateButton}
          >
            {creatingPersonalizationLink
              ? "Generating Link...."
              : "Generate Link"}
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};
